import { ComponentResolver } from '@aspendental/shared-components-web';
import { IGoogleMap } from '@aspendental/shared-components-web/lib/components/GoogleMap/GoogleMap';
import { ImagePriorityContext } from '@aspendental/shared-components-web/lib/utils/images/imageOptimization';

import {
	BffBrandType,
	Maybe,
	Provider,
	ProviderDetailsPageContentsCollection,
	ProviderDetailsPageContentsItem,
	ProviderDetailsPageSecondaryContentsCollection,
	ProviderDetailsPageSecondaryContentsItem,
	ReviewWithSummary,
} from '../../lib/__generated/sdk';
import ProviderAbout from './ProviderAbout';
import FeaturedProviderReviews from './FeaturedProviderReviews';
import ProviderHero from './ProviderHero';
import ProviderInsurance from './ProviderInsurance';
import ProviderOffices from './ProviderOffices';
import ProviderQuotes from './ProviderQuotes';

export interface ExtendedProvider extends Provider {
	providerImages: {
		url: string;
	}[];
	brand: BffBrandType;
}

export interface IProviderDetailsPage {
	reviews?: ReviewWithSummary;
	selectedReviews?: string[] | null;
	provider: ExtendedProvider;
	config: BrandThemeConfig;
	contentsCollection?: Maybe<ProviderDetailsPageContentsCollection>;
	secondaryContentsCollection?: Maybe<ProviderDetailsPageSecondaryContentsCollection>;
	tagTheme?: TagThemeType;
	showProviderPageInsuranceInfo?: boolean;
	googleMapsData?: IGoogleMap;
}

export default function ProviderDetailsPage({
	reviews,
	selectedReviews,
	provider,
	config,
	contentsCollection,
	secondaryContentsCollection,
	tagTheme,
	showProviderPageInsuranceInfo = false,
	googleMapsData,
}: Readonly<IProviderDetailsPage>) {
	return (
		<>
			<ImagePriorityContext.Provider value={0}>
				<ProviderHero provider={provider} config={config} reviews={reviews} selectedReviews={selectedReviews} />
			</ImagePriorityContext.Provider>
			{config?.name === 'ClearChoice' && <ProviderQuotes provider={provider} />}
			{provider.biography && <ProviderAbout provider={provider} config={config} />}
			{/* <ProviderSpecialties /> */}
			{contentsCollection?.items?.map((content: Maybe<ProviderDetailsPageContentsItem>, index: number) => {
				if (!content?.__typename || Object.keys(content).length <= 2) {
					console.warn(
						`⚠️⚠️⚠️ Shared Components Web - Provider Details Page - NPI - ${
							provider.npi || ''
						} - Content - No content __typename found for ${content?.sys?.id || 'Unknown entryId'} ⚠️⚠️⚠️`
					);
					return null;
				}

				return (
					<ComponentResolver
						key={`${content.sys.id}-${content.__typename}-${index}`}
						// @ts-ignore
						componentProps={content}
						typename={content.__typename}
					/>
				);
			})}
			{provider?.providerLocations?.some(({ facility }) => facility) && (
				<ProviderOffices
					provider={provider}
					config={config}
					tagTheme={tagTheme}
					googleMapsData={googleMapsData}
				/>
			)}
			<FeaturedProviderReviews
				reviews={reviews}
				selectedReviews={selectedReviews}
				title="Reviews & Testimonials"
				providerName={`Dr. ${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim()}
			/>
			{showProviderPageInsuranceInfo && <ProviderInsurance provider={provider} config={config} />}
			{secondaryContentsCollection?.items?.map(
				(content: Maybe<ProviderDetailsPageSecondaryContentsItem>, index: number) => {
					if (!content?.__typename || Object.keys(content).length <= 2) {
						console.warn(
							`⚠️⚠️⚠️ Shared Components Web - Provider Details Page - NPI - ${
								provider.npi || ''
							} - Secondary Content - No content __typename found for ${
								content?.sys?.id || 'Unknown entryId'
							} ⚠️⚠️⚠️`
						);
						return null;
					}

					// @ts-ignore
					return (
						<ComponentResolver
							key={`${content.sys.id}-${content.__typename}-${index}`}
							// @ts-ignore
							componentProps={content}
							typename={content.__typename}
						/>
					);
				}
			)}
		</>
	);
}
